import { encode } from 'blurhash';

/**
 * Chuyển đổi hình ảnh thành blurhash string
 * @param imageFile - File hình ảnh hoặc Blob
 * @param componentX - Số lượng component theo chiều ngang (1-9, mặc định 4)
 * @param componentY - Số lượng component theo chiều dọc (1-9, mặc định 4)
 * @returns Promise<string> - Blurhash string
 */
export async function imageToBlurHash(
  imageFile: File | Blob,
  componentX = 4,
  componentY = 4
): Promise<{ blurHash: string; width: number; height: number }> {
  // Validate components
  if (componentX < 1 || componentX > 9 || componentY < 1 || componentY > 9) {
    throw new Error('Components must be between 1 and 9');
  }

  return new Promise((resolve) => {
    if (!imageFile.type.startsWith('image/')) {
      resolve({ blurHash: '', width: 0, height: 0 });
      return;
    }
    // Tạo URL từ file
    const imageUrl = URL.createObjectURL(imageFile);

    // Tạo image element
    const img = new Image();

    img.onload = () => {
      try {
        // Tạo canvas để lấy pixel data
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('Cannot get canvas context');
        }

        // Set canvas size theo kích thước ảnh
        canvas.width = img.width;
        canvas.height = img.height;

        // Vẽ image lên canvas
        ctx.drawImage(img, 0, 0);

        // Lấy image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        // Encode thành blurhash
        const blurHash = encode(imageData.data, imageData.width, imageData.height, componentX, componentY);

        // Cleanup
        URL.revokeObjectURL(imageUrl);

        resolve({ blurHash, width: imageData.width, height: imageData.height });
      } catch (error) {
        URL.revokeObjectURL(imageUrl);
        resolve({ blurHash: '', width: 0, height: 0 });
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      resolve({ blurHash: '', width: 0, height: 0 });
    };

    // Load image
    img.src = imageUrl;
  });
}

/**
 * Chuyển đổi hình ảnh từ URL thành blurhash
 * @param imageUrl - URL của hình ảnh
 * @param componentX - Số lượng component theo chiều ngang (1-9, mặc định 4)
 * @param componentY - Số lượng component theo chiều dọc (1-9, mặc định 4)
 * @returns Promise<string> - Blurhash string
 */
export async function imageUrlToBlurHash(imageUrl: string, componentX = 4, componentY = 4): Promise<string> {
  // Validate components
  if (componentX < 1 || componentX > 9 || componentY < 1 || componentY > 9) {
    throw new Error('Components must be between 1 and 9');
  }

  return new Promise((resolve, reject) => {
    const img = new Image();

    // Set CORS nếu cần thiết
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('Cannot get canvas context');
        }

        canvas.width = img.width;
        canvas.height = img.height;

        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        const blurHash = encode(imageData.data, imageData.width, imageData.height, componentX, componentY);

        resolve(blurHash);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image from URL'));
    };

    img.src = imageUrl;
  });
}

/**
 * Tối ưu hóa kích thước ảnh trước khi tạo blurhash (để tăng performance)
 * @param imageFile - File hình ảnh
 * @param maxWidth - Chiều rộng tối đa (mặc định 40px)
 * @param maxHeight - Chiều cao tối đa (mặc định 40px)
 * @param componentX - Số lượng component theo chiều ngang
 * @param componentY - Số lượng component theo chiều dọc
 * @returns Promise<string> - Blurhash string
 */
export async function imageToBlurHashOptimized(
  imageFile: File | Blob,
  maxWidth = 40,
  maxHeight = 40,
  componentX = 4,
  componentY = 4
): Promise<string> {
  return new Promise((resolve, reject) => {
    const imageUrl = URL.createObjectURL(imageFile);
    const img = new Image();

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('Cannot get canvas context');
        }

        // Tính toán kích thước mới giữ nguyên tỷ lệ
        const aspectRatio = img.width / img.height;
        let newWidth = maxWidth;
        let newHeight = maxHeight;

        if (aspectRatio > 1) {
          newHeight = maxWidth / aspectRatio;
        } else {
          newWidth = maxHeight * aspectRatio;
        }

        canvas.width = Math.round(newWidth);
        canvas.height = Math.round(newHeight);

        // Resize và vẽ image
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        const blurHash = encode(imageData.data, imageData.width, imageData.height, componentX, componentY);

        URL.revokeObjectURL(imageUrl);
        resolve(blurHash);
      } catch (error) {
        URL.revokeObjectURL(imageUrl);
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = imageUrl;
  });
}
